import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';

const KIND_ICON = {
  object: '🏢',
  device: '⚙',
  sim: '📶',
  subnet: '🌐',
};

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [hover, setHover] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const nav = useNavigate();
  const seqRef = useRef(0);

  useEffect(() => {
    const onKey = (e) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
      if (isCmdK) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
      setQ('');
      setResults([]);
      setHover(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!q.trim()) { setResults([]); return; }
    const mySeq = ++seqRef.current;
    setLoading(true);
    const id = setTimeout(() => {
      api.get('/api/search', { params: { q } })
        .then(({ data }) => {
          if (mySeq !== seqRef.current) return;
          setResults(data.results || []);
          setHover(0);
        })
        .catch(() => {})
        .finally(() => { if (mySeq === seqRef.current) setLoading(false); });
    }, 180);
    return () => clearTimeout(id);
  }, [q, open]);

  const goto = (r) => {
    setOpen(false);
    nav(r.url);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHover((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHover((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && results[hover]) { e.preventDefault(); goto(results[hover]); }
  };

  if (!open) return null;

  return (
    <div className="cmdk-backdrop" onClick={() => setOpen(false)}>
      <div className="cmdk" onClick={(e) => e.stopPropagation()}>
        <div className="cmdk-input">
          <span className="cmdk-icon">🔎</span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Поиск: объект, устройство, IP, ICCID, гос.номер, организация…"
          />
          <span className="cmdk-hint">esc</span>
        </div>
        <div className="cmdk-results">
          {loading && <div className="cmdk-empty">Поиск…</div>}
          {!loading && q && results.length === 0 && <div className="cmdk-empty">Ничего не найдено</div>}
          {!loading && !q && <div className="cmdk-empty muted">Введите запрос. Cmd/Ctrl+K — открыть/закрыть.</div>}
          {results.map((r, i) => (
            <div
              key={`${r.kind}-${r.id}`}
              className={`cmdk-row ${i === hover ? 'on' : ''}`}
              onMouseEnter={() => setHover(i)}
              onClick={() => goto(r)}
            >
              <span className="cmdk-row-icon">{KIND_ICON[r.kind] || '•'}</span>
              <div className="cmdk-row-text">
                <div className="cmdk-row-title">{r.title}</div>
                {r.subtitle && <div className="cmdk-row-sub muted small">{r.subtitle}</div>}
              </div>
              <span className="cmdk-row-kind muted small">{r.kind}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
